import { supportsNormalizedTemplate } from '../helpers/patterns/patternCoordinates';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Divider, Form, Input, InputNumber, message, Radio, Space, Switch, Table, Typography, Modal } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import {
    buildMissionPatternReturnUrl,
    readMissionPatternHandoff,
    writeMissionPatternResult,
    type MissionPatternHandoffResult,
} from '../components/Operations/missionPatternHandoff';
import '../style/PatternCreate.css';

const { Title, Text } = Typography;

interface Point {
    index: number;
    xNorm: number;
    yNorm: number;
    xMeters: number;
    yMeters: number;
    label?: string;
}

interface AreaTypeInfo {
    maxVertices?: number | null;
    templateImage?: string | null;
    patternCoordinateMode?: 'gps' | 'normalized' | null;
    id: number;
    name: string;
    templateWidthMeters?: number;
    templateHeightMeters?: number;
    templateDimensions?: {
        widthMeters?: number;
        heightMeters?: number;
    };
}

const PatternCreateTemplate: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const areaTypeId = searchParams.get('areaTypeId');
    const patternId = searchParams.get('patternId');
    const missionDraftKey = searchParams.get('missionDraftKey');
    const [missionHandoff] = useState(() => readMissionPatternHandoff(missionDraftKey));
    const isMissionRouteFlow = Boolean(missionDraftKey && missionHandoff && !patternId);
    const [effectiveAreaTypeId, setEffectiveAreaTypeId] = useState<string | null>(areaTypeId);
    
    const [form] = Form.useForm();
    const [areaType, setAreaType] = useState<AreaTypeInfo | null>(null);
    const [templateUrl, setTemplateUrl] = useState<string | null>(null);
    const templateObjectUrl = useRef<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveAsPattern, setSaveAsPattern] = useState(true);
    const [editingPatternId, setEditingPatternId] = useState<number | null>(patternId ? Number(patternId) : null);
    const [editingPointsLoaded, setEditingPointsLoaded] = useState(false);
    
    const [mode, setMode] = useState<'manual' | 'grid'>('manual');
    const [points, setPoints] = useState<Point[]>([]);
    const [gridRows, setGridRows] = useState(3);
    const [gridCols, setGridCols] = useState(4);
    const [gridOffsetLeft, setGridOffsetLeft] = useState(5);
    const [gridOffsetRight, setGridOffsetRight] = useState(5);
    const [gridOffsetTop, setGridOffsetTop] = useState(5);
    const [gridOffsetBottom, setGridOffsetBottom] = useState(5);
    const [gridPattern, setGridPattern] = useState<'standard' | 's-pattern'>('standard');
    const [gridStartCorner, setGridStartCorner] = useState<'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'>('bottom-left');
    const [gridPrimaryDirection, setGridPrimaryDirection] = useState<'horizontal' | 'vertical'>('horizontal');
    
    const canvasRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [draggedPoint, setDraggedPoint] = useState<number | null>(null);

    const returnToMission = (result: MissionPatternHandoffResult) => {
        if (!missionDraftKey || !writeMissionPatternResult(missionDraftKey, result)) {
            message.error('The mission draft is no longer available. Please return to Operations and start again.');
            return false;
        }
        navigate(buildMissionPatternReturnUrl(missionDraftKey));
        return true;
    };

    const handleCancel = () => {
        if (isMissionRouteFlow) {
            returnToMission({ status: 'cancelled' });
            return;
        }
        navigate('/patterns');
    };

    // Default template dimensions if not provided
    const templateWidth = areaType?.templateWidthMeters ?? areaType?.templateDimensions?.widthMeters ?? 100;
    const templateHeight = areaType?.templateHeightMeters ?? areaType?.templateDimensions?.heightMeters ?? 60;

    // Calculate image bounds within container
    useEffect(() => {
        if (!templateUrl || !canvasRef.current) return;

        const img = new Image();
        imgRef.current = img;
        
        const calculateBounds = () => {
            const container = canvasRef.current;
            const image = imgRef.current;
            if (!container || !image || !image.complete) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let displayWidth, displayHeight, offsetX, offsetY;

            if (containerAspect > imageAspect) {
                // Container is wider - image constrained by height
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                // Container is taller - image constrained by width
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        img.onload = () => {
            calculateBounds();
        };
        img.src = templateUrl;

        // Add resize observer to recalculate bounds on container resize
        const resizeObserver = new ResizeObserver(() => {
            calculateBounds();
        });

        if (canvasRef.current) {
            resizeObserver.observe(canvasRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            imgRef.current = null;
        };
    }, [templateUrl]);

    useEffect(() => {
        if (!effectiveAreaTypeId) {
            if (!patternId) {
                message.error('Area type ID is required');
                navigate('/patterns');
            }
            return;
        }

        const loadAreaType = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${effectiveAreaTypeId}`, { withCredentials: true });
                const data = res.data?.data || res.data;
                setAreaType(data);
                if (!supportsNormalizedTemplate(data)) {
                    message.error('This area type uses GPS points. Select an area and create a GPS pattern; its image is not a normalized template.');
                    navigate('/patterns');
                    return;
                }

                // Load template image with cache-busting
                const imgRes = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${effectiveAreaTypeId}/template-image/download?t=${Date.now()}`, {
                    withCredentials: true,
                    responseType: 'blob',
                });
                const url = URL.createObjectURL(imgRes.data as Blob);
                templateObjectUrl.current = url;
                setTemplateUrl(url);
            } catch (err) {
                console.error('Failed to load area type', err);
                message.error('Failed to load area type information');
                navigate('/patterns');
            } finally {
                setLoading(false);
            }
        };

        loadAreaType();

        return () => {
            if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
        };
    }, [effectiveAreaTypeId, navigate, patternId]);

    useEffect(() => {
        if (!patternId) return;
        const loadPattern = async () => {
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${patternId}`, { withCredentials: true });
                const data = res.data?.data || res.data;
                if (!data) return;
                if (data.coordSystem !== 'local' || !(data.areaTypeId || data.areaType?.id)) {
                    message.error('This is not a reusable normalized template pattern. Use its area-specific editor.');
                    navigate('/patterns');
                    return;
                }
                form.setFieldsValue({
                    name: data.name,
                    type: data.patternType,
                });
                if (!effectiveAreaTypeId && data.areaType?.id) {
                    setEffectiveAreaTypeId(String(data.areaType.id));
                }
                setEditingPatternId(data.id);
            } catch (err) {
                console.error('Failed to load pattern', err);
                message.error('Failed to load pattern for editing');
                navigate('/patterns');
            }
        };
        loadPattern();
    }, [patternId, form, effectiveAreaTypeId, navigate]);

    useEffect(() => {
        if (!editingPatternId || !areaType || editingPointsLoaded) return;
        const loadPatternPoints = async () => {
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${editingPatternId}/points`, { withCredentials: true });
                const data = res.data?.data || res.data || [];
                const mapped = data
                    .filter((p: any) => p.xLocal != null && p.yLocal != null)
                    .map((p: any, idx: number) => {
                        const xNorm = Number(p.xLocal ?? 0);
                        const yNorm = Number(p.yLocal ?? 0);
                        return {
                            index: p.pointIndex ?? idx + 1,
                            xNorm,
                            yNorm,
                            xMeters: xNorm * templateWidth,
                            yMeters: yNorm * templateHeight,
                            label: p.label || undefined,
                        };
                    });
                setPoints(mapped);
                setEditingPointsLoaded(true);
            } catch (err) {
                console.warn('Failed to load pattern points', err);
            }
        };
        loadPatternPoints();
    }, [editingPatternId, areaType, editingPointsLoaded, templateWidth, templateHeight]);

    const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (mode !== 'manual') return;

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect || imageBounds.width === 0) return;

        // Calculate click position relative to container
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // Check if click is within image bounds
        if (
            clickX < imageBounds.left ||
            clickX > imageBounds.left + imageBounds.width ||
            clickY < imageBounds.top ||
            clickY > imageBounds.top + imageBounds.height
        ) {
            message.warning('Click within the template image area');
            return;
        }

        // Calculate normalized coordinates relative to image (Cartesian: 0=bottom, 1=top)
        const x = (clickX - imageBounds.left) / imageBounds.width;
        const y = 1 - (clickY - imageBounds.top) / imageBounds.height;

        addPoint(x, y);
    };

    const addPoint = (xNorm: number, yNorm: number) => {
        const newPoint: Point = {
            index: points.length + 1,
            xNorm,
            yNorm,
            xMeters: xNorm * templateWidth,
            yMeters: yNorm * templateHeight,
        };
        setPoints([...points, newPoint]);
    };

    const handleMouseDown = (e: React.MouseEvent, index: number) => {
        e.stopPropagation();
        setDraggedPoint(index);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (draggedPoint === null || imageBounds.width === 0) return;

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        // Calculate position relative to container
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // Calculate normalized coordinates relative to image (Cartesian: 0=bottom, 1=top)
        const x = Math.max(0, Math.min(1, (clickX - imageBounds.left) / imageBounds.width));
        const y = Math.max(0, Math.min(1, 1 - (clickY - imageBounds.top) / imageBounds.height));

        setPoints(points.map((p, i) =>
            i === draggedPoint
                ? { ...p, xNorm: x, yNorm: y, xMeters: x * templateWidth, yMeters: y * templateHeight }
                : p
        ));
    };

    const handleMouseUp = () => {
        setDraggedPoint(null);
    };

    const generateGrid = () => {
        const newPoints: Point[] = [];
        let index = 1;

        // Available space between edge offsets
        const availableWidth = templateWidth - gridOffsetLeft - gridOffsetRight;
        const availableHeight = templateHeight - gridOffsetTop - gridOffsetBottom;

        const spacingX = gridCols > 1 ? availableWidth / (gridCols - 1) : 0;
        const spacingY = gridRows > 1 ? availableHeight / (gridRows - 1) : 0;

        // Note: coordinate system is Cartesian — y=0 at bottom, y=templateHeight at top.
        // gridOffsetTop = margin from the y-min edge (bottom of template image)
        // gridOffsetBottom = margin from the y-max edge (top of template image)
        const startFromRight = gridStartCorner === 'bottom-right' || gridStartCorner === 'top-right';
        const startFromTop = gridStartCorner === 'top-left' || gridStartCorner === 'top-right';

        const xStart = startFromRight ? (templateWidth - gridOffsetRight) : gridOffsetLeft;
        const yStart = startFromTop ? (templateHeight - gridOffsetBottom) : gridOffsetTop;
        const xSign = startFromRight ? -1 : 1;
        const ySign = startFromTop ? -1 : 1; // negative = moving toward smaller y (toward bottom of template)

        if (gridPrimaryDirection === 'horizontal') {
            // Outer loop: rows; inner loop: columns
            for (let row = 0; row < gridRows; row++) {
                const yMeters = yStart + ySign * row * spacingY;
                const baseColOrder = Array.from({ length: gridCols }, (_, i) => i);
                const colOrder = (gridPattern === 's-pattern' && row % 2 === 1)
                    ? [...baseColOrder].reverse()
                    : baseColOrder;
                for (const col of colOrder) {
                    const xMeters = xStart + xSign * col * spacingX;
                    if (xMeters >= 0 && xMeters <= templateWidth && yMeters >= 0 && yMeters <= templateHeight) {
                        newPoints.push({ index: index++, xNorm: xMeters / templateWidth, yNorm: yMeters / templateHeight, xMeters, yMeters });
                    }
                }
            }
        } else {
            // Outer loop: columns; inner loop: rows
            for (let col = 0; col < gridCols; col++) {
                const xMeters = xStart + xSign * col * spacingX;
                const baseRowOrder = Array.from({ length: gridRows }, (_, i) => i);
                const rowOrder = (gridPattern === 's-pattern' && col % 2 === 1)
                    ? [...baseRowOrder].reverse()
                    : baseRowOrder;
                for (const row of rowOrder) {
                    const yMeters = yStart + ySign * row * spacingY;
                    if (xMeters >= 0 && xMeters <= templateWidth && yMeters >= 0 && yMeters <= templateHeight) {
                        newPoints.push({ index: index++, xNorm: xMeters / templateWidth, yNorm: yMeters / templateHeight, xMeters, yMeters });
                    }
                }
            }
        }

        if (newPoints.length === 0) {
            message.warning('No points fit within template bounds. Adjust grid parameters.');
            return;
        }

        setPoints(newPoints);
        message.success(`Generated ${newPoints.length} points (spacing: ${spacingX.toFixed(1)}m × ${spacingY.toFixed(1)}m)`);
    };

    const updatePoint = (index: number, field: 'xMeters' | 'yMeters' | 'label', value: any) => {
        setPoints(points.map((p, i) => {
            if (i !== index) return p;
            if (field === 'label') return { ...p, label: value };
            
            // Update from meters, recalculate normalized
            if (field === 'xMeters') {
                const xNorm = value / templateWidth;
                return { ...p, xNorm, xMeters: value };
            } else {
                const yNorm = value / templateHeight;
                return { ...p, yNorm, yMeters: value };
            }
        }));
    };

    const deletePoint = (index: number) => {
        setPoints(points.filter((_, i) => i !== index).map((p, i) => ({ ...p, index: i + 1 })));
    };

    const clearAll = () => {
        setPoints([]);
    };

    const confirmPatternChange = () => new Promise<boolean>((resolve) => {
        Modal.confirm({
            title: 'Update pattern?',
            content: (
                <div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#cf1322' }}>
                        Updating points will remove existing point links.
                    </div>
                    <Alert
                        type="error"
                        showIcon
                        message="Downstream data will be deleted"
                        description="Reports, conversations, analysis summaries, learning data, and mission points linked to existing points will be removed."
                    />
                    <Alert
                        type="warning"
                        showIcon
                        message="Measurements will be kept but unlinked"
                        description="Existing measurements will remain but their point link will be cleared."
                        style={{ marginTop: 8 }}
                    />
                </div>
            ),
            okText: 'Continue',
            cancelText: 'Cancel',
            okType: 'danger',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    const handleSave = async () => {
        if (!supportsNormalizedTemplate(areaType)) {
            message.error('A standard four-corner template is required for normalized points');
            return;
        }
        try {
            const values = saveAsPattern || !isMissionRouteFlow
                ? await form.validateFields()
                : form.getFieldsValue();
            
            if (points.length === 0) {
                message.error('Add at least one point');
                return;
            }

            if (editingPatternId) {
                const confirmed = await confirmPatternChange();
                if (!confirmed) return;
            }

            setSaving(true);

            const pointsPayload = points.map((p, i) => {
                const point: any = {
                    pointIndex: i + 1,
                    xLocal: Number(p.xNorm.toFixed(6)),
                    yLocal: Number(p.yNorm.toFixed(6)),
                };
                if (p.label && p.label.trim()) {
                    point.label = p.label.trim();
                }
                return point;
            });

            if (isMissionRouteFlow && !saveAsPattern) {
                returnToMission({
                    status: 'mission-only',
                    coordSystem: 'local',
                    definition: { boxWidth: 1, boxHeight: 1 },
                    points: pointsPayload,
                });
                return;
            }

            const payload = {
                name: values.name,
                patternType: values.type || 'custom',
                coordSystem: 'local',
                definition: { boxWidth: 1, boxHeight: 1 },
                areaTypeId: effectiveAreaTypeId ? parseInt(effectiveAreaTypeId, 10) : undefined,
                isGlobal: false,
            };

            let targetPatternId = editingPatternId;
            if (editingPatternId) {
                await axios.put(`${ENDPOINTS.PATTERNS}/${editingPatternId}`, payload, { withCredentials: true });
            } else {
                const createRes = await axios.post(ENDPOINTS.PATTERNS, payload, { withCredentials: true });
                targetPatternId = createRes.data?.data?.id || createRes.data?.id;
            }

            if (targetPatternId) {
                console.log('[PatternCreate] Saving points:', pointsPayload);
                console.log('[PatternCreate] Pattern ID:', patternId);
                
                const bulkPayload = {
                    points: pointsPayload,
                    replaceAll: true,
                };
                console.log('[PatternCreate] Full bulk payload:', JSON.stringify(bulkPayload, null, 2));
                
                try {
                    const pointsRes = await axios.post(
                        `${ENDPOINTS.PATTERNS}/${targetPatternId}/points/bulk`,
                        bulkPayload,
                        { withCredentials: true }
                    );
                    console.log('[PatternCreate] Points save success:', pointsRes.data);
                } catch (pointsErr: any) {
                    console.error('[PatternCreate] Points save failed:', pointsErr.response?.data);
                    throw new Error(`Failed to save points: ${pointsErr.response?.data?.message || pointsErr.message}`);
                }
            }

            message.success(editingPatternId ? 'Pattern updated successfully' : 'Pattern created successfully');
            if (isMissionRouteFlow && targetPatternId) {
                returnToMission({ status: 'saved', patternId: Number(targetPatternId) });
            } else {
                navigate('/patterns');
            }
        } catch (err: any) {
            console.error('Failed to create pattern', err);
            console.error('Error response:', err.response?.data);
            const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to create pattern';
            message.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pattern-create-container pattern-create-template-page">
            <Card>
                <div style={{ marginBottom: 16 }}>
                    <Button className="pattern-create-back-button" icon={<ArrowLeftOutlined />} onClick={handleCancel}>
                        {isMissionRouteFlow ? 'Back to mission' : 'Back to Patterns'}
                    </Button>
                </div>

                <Alert showIcon type="info" style={{ marginBottom: 16 }}
                    message="Normalized template pattern — reusable across compatible areas"
                    description="Points are saved as xLocal/yLocal from 0 to 1 on this standard four-corner template. For fixed GPS points, irregular areas, or a non-standard image, use an area-specific GPS pattern instead." />
                <Title level={3}>{editingPatternId ? 'Edit Pattern' : 'Create Pattern'} - {areaType?.name}</Title>
                <Text type="secondary">
                    Template dimensions: {templateWidth}m × {templateHeight}m
                </Text>

                <div className="pattern-create-layout">
                    <div className="pattern-create-sidebar">
                        <Form form={form} layout="vertical" className="pattern-create-metadata">
                            <Form.Item label="Pattern Name" name="name" rules={saveAsPattern || !isMissionRouteFlow ? [{ required: true }] : []}>
                                <Input
                                    placeholder="e.g., Standard Grid"
                                    disabled={isMissionRouteFlow && !saveAsPattern}
                                />
                            </Form.Item>
                            <Form.Item label="Pattern Type" name="type">
                                <Input placeholder="e.g., grid, lines, custom" />
                            </Form.Item>
                        </Form>

                        {isMissionRouteFlow ? (
                            <Alert
                                type="info"
                                showIcon
                                message="Return this route to your mission"
                                description={(
                                    <Space direction="vertical" size={4}>
                                        <Space>
                                            <Switch checked={saveAsPattern} onChange={setSaveAsPattern} />
                                            <Text strong>Save as a reusable pattern</Text>
                                        </Space>
                                        <Text type="secondary">
                                            Enabled by default. Turn it off to keep these points only in the current mission.
                                        </Text>
                                    </Space>
                                )}
                                style={{ marginTop: 16 }}
                            />
                        ) : null}

                        <div className="pattern-create-tool-mode" style={{ marginTop: 24 }}>
                            <Text strong>Tool Mode</Text>
                            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ marginTop: 8, display: 'block' }}>
                                <Space direction="vertical">
                                    <Radio value="manual">Manual (Click to add)</Radio>
                                    <Radio value="grid">Grid Generator</Radio>
                                </Space>
                            </Radio.Group>
                        </div>

                        {mode === 'grid' && (
                            <div className="pattern-create-grid-config" style={{ marginTop: 16 }}>
                                <Text strong>Grid Configuration</Text>
                                <div style={{ marginTop: 8 }}>
                                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Rows:</Text>
                                            <InputNumber 
                                                min={1} 
                                                max={20} 
                                                value={gridRows} 
                                                onChange={(v) => setGridRows(v || 3)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Columns:</Text>
                                            <InputNumber 
                                                min={1} 
                                                max={20} 
                                                value={gridCols} 
                                                onChange={(v) => setGridCols(v || 4)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        
                                        <Divider style={{ margin: '8px 0' }}>Edge Offsets (m)</Divider>
                                        
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Left:</Text>
                                            <InputNumber 
                                                min={0} 
                                                max={templateWidth / 2} 
                                                step={0.5}
                                                value={gridOffsetLeft} 
                                                onChange={(v) => setGridOffsetLeft(v || 5)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Right:</Text>
                                            <InputNumber 
                                                min={0} 
                                                max={templateWidth / 2} 
                                                step={0.5}
                                                value={gridOffsetRight} 
                                                onChange={(v) => setGridOffsetRight(v || 5)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Top:</Text>
                                            <InputNumber 
                                                min={0} 
                                                max={templateHeight / 2} 
                                                step={0.5}
                                                value={gridOffsetTop} 
                                                onChange={(v) => setGridOffsetTop(v || 5)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Bottom:</Text>
                                            <InputNumber 
                                                min={0} 
                                                max={templateHeight / 2} 
                                                step={0.5}
                                                value={gridOffsetBottom} 
                                                onChange={(v) => setGridOffsetBottom(v || 5)} 
                                                style={{ marginLeft: 8, width: 80 }} 
                                            />
                                        </div>
                                        
                                        <Divider style={{ margin: '8px 0' }}>Start Corner</Divider>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                            <Radio.Group
                                                value={gridStartCorner}
                                                onChange={(e) => setGridStartCorner(e.target.value)}
                                            >
                                                <Radio value="top-left" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↖ Top-Left</Radio>
                                                <Radio value="top-right" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↗ Top-Right</Radio>
                                                <Radio value="bottom-left" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↙ Bottom-Left</Radio>
                                                <Radio value="bottom-right" style={{ display: 'block', fontSize: 11 }}>↘ Bottom-Right</Radio>
                                            </Radio.Group>
                                        </div>

                                        <Divider style={{ margin: '8px 0' }}>Scan Direction</Divider>

                                        <Radio.Group
                                            value={gridPrimaryDirection}
                                            onChange={(e) => setGridPrimaryDirection(e.target.value)}
                                        >
                                            <Radio value="horizontal" style={{ display: 'block', fontSize: 12, marginBottom: 2 }}>Horizontal (row by row)</Radio>
                                            <Radio value="vertical" style={{ display: 'block', fontSize: 12 }}>Vertical (col by col)</Radio>
                                        </Radio.Group>

                                        <Divider style={{ margin: '8px 0' }}>Pattern Order</Divider>
                                        
                                        <Radio.Group 
                                            value={gridPattern} 
                                            onChange={(e) => setGridPattern(e.target.value)}
                                        >
                                            <Radio value="standard" style={{ display: 'block', fontSize: 12 }}>Standard</Radio>
                                            <Radio value="s-pattern" style={{ display: 'block', fontSize: 12 }}>S-Pattern (zigzag)</Radio>
                                        </Radio.Group>
                                        
                                        <Button type="primary" onClick={generateGrid} block style={{ marginTop: 12 }}>
                                            Generate Grid
                                        </Button>
                                        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                                            Spacing calculated automatically
                                        </Text>
                                    </Space>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 24 }}>
                            <Button danger onClick={clearAll} block>
                                Clear All Points
                            </Button>
                        </div>
                    </div>

                    <div className="pattern-create-main">
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Interactive Template Canvas</Text>
                        <div
                            ref={canvasRef}
                            className="pattern-canvas pattern-create-template-canvas"
                            onClick={handleCanvasClick}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{
                                backgroundImage: templateUrl ? `url(${templateUrl})` : undefined,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '500px',
                                border: '2px solid #d9d9d9',
                                borderRadius: 8,
                                cursor: mode === 'manual' ? 'crosshair' : 'default',
                            }}
                        >
                            {points.map((point, index) => (
                                <div
                                    key={index}
                                    className="pattern-point"
                                    style={{
                                        position: 'absolute',
                                        left: `${imageBounds.left + point.xNorm * imageBounds.width}px`,
                                        top: `${imageBounds.top + (1 - point.yNorm) * imageBounds.height}px`,
                                        transform: 'translate(-50%, -50%)',
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        background: '#fa8c16',
                                        border: '2px solid white',
                                        cursor: 'move',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                        fontWeight: 'bold',
                                        color: 'white',
                                    }}
                                    onMouseDown={(e) => handleMouseDown(e, index)}
                                >
                                    {point.index}
                                </div>
                            ))}
                        </div>

                        <div className="pattern-create-points-table" style={{ marginTop: 16 }}>
                            <Text strong>Points ({points.length})</Text>
                            <Table
                                size="small"
                                dataSource={points}
                                rowKey="index"
                                pagination={false}
                                style={{ marginTop: 8 }}
                                scroll={{ y: 200 }}
                                columns={[
                                    { title: '#', dataIndex: 'index', width: 40 },
                                    {
                                        title: 'X (m)',
                                        dataIndex: 'xMeters',
                                        width: 100,
                                        render: (val, record, index) => (
                                            <InputNumber
                                                size="small"
                                                min={0}
                                                max={templateWidth}
                                                step={0.1}
                                                value={val}
                                                onChange={(v) => updatePoint(index, 'xMeters', v || 0)}
                                            />
                                        ),
                                    },
                                    {
                                        title: 'Y (m)',
                                        dataIndex: 'yMeters',
                                        width: 100,
                                        render: (val, record, index) => (
                                            <InputNumber
                                                size="small"
                                                min={0}
                                                max={templateHeight}
                                                step={0.1}
                                                value={val}
                                                onChange={(v) => updatePoint(index, 'yMeters', v || 0)}
                                            />
                                        ),
                                    },
                                    {
                                        title: 'Label',
                                        dataIndex: 'label',
                                        render: (val, record, index) => (
                                            <Input
                                                size="small"
                                                value={val}
                                                onChange={(e) => updatePoint(index, 'label', e.target.value)}
                                                placeholder="optional"
                                            />
                                        ),
                                    },
                                    {
                                        title: '',
                                        width: 50,
                                        render: (_, record, index) => (
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => deletePoint(index)}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        <div className="pattern-create-actions" style={{ marginTop: 24, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={handleCancel}>Cancel</Button>
                            <Button type="primary" onClick={handleSave} loading={saving}>
                                {editingPatternId
                                    ? 'Save Changes'
                                    : isMissionRouteFlow && !saveAsPattern
                                        ? 'Use in this mission only'
                                        : isMissionRouteFlow
                                            ? 'Save pattern and continue'
                                            : 'Save Pattern'}
                            </Button>
                            </Space>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PatternCreateTemplate;
